import { useEffect, useRef, useMemo } from 'react';
import { useLocation, useNavigate, Outlet } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

// Redux
import {
  handleB2BCallback,
  selectAuth,
  selectIsAuthenticated,
  selectIsLoading,
  logoutAction
} from 'redux/slices/authSlice';
import { getRoleMisMatch } from 'redux/slices/application';

// Utils & Constants
import { landingPathForRole, mapRoleToAppRole } from 'utils/roles';
import {
  getCurrentOrgSlug,
  getTenantScopedStorageKey,
  stripStatePrefix,
  buildAppPath,
} from 'utils/tenant';

// Components
import { AppShell } from '@/pages/shared/app/AppShell';
import CardLoader from 'wrapper/card-loader';
import EmailVerified from 'wrapper/email-verified';
import SessionExpire from 'wrapper/session-expire/SessionExpire';
import ErrorPage from './ErrorPage';
import MiraUnifiedLogin from './MiraUnifiedLogin';
import useAuthBootstrap from 'hooks/useAuthBootstrap';

const Login = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  // Debug: log whenever Login component renders
  if (!import.meta.env.PROD) {
    console.log('[Login] Component mounted/rendered - pathname:', location.pathname);
    console.log('[Login] Current search params:', Object.fromEntries(new URLSearchParams(location.search)));
  }

  const navigationDoneRef = useRef(false);
  const roleMismatch = useSelector(getRoleMisMatch);
  
  // Auth state from Redux
  const authState = useSelector(selectAuth);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const isLoading = useSelector(selectIsLoading);
  const { authLoaded } = useAuthBootstrap();

  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const orgSlug = getCurrentOrgSlug();
  const redirectStorageKey = useMemo(
    () => getTenantScopedStorageKey('redirectAfterLogin', orgSlug),
    [orgSlug]
  );
  const error = searchParams.get('error');
  const pathname = location.pathname;
  const statelessPath = stripStatePrefix(pathname);

  const shouldRenderOutlet = statelessPath === '/onboard' ||
                             statelessPath.startsWith('/auth/') ||
                             statelessPath === '/login/consumer' ||
                             statelessPath === '/login/b2b' ||
                             statelessPath === '/account-selection';

  // A document opened in a tab of its own is the viewer alone — no sidebar,
  // header or chat around it.
  const shouldRenderOutletWhenAuthenticated = statelessPath === '/onboard' ||
                                              statelessPath.startsWith('/auth/') ||
                                              statelessPath.startsWith('/document/');

  const isProtectedRoute = statelessPath === '/dashboard' ||
                          statelessPath === '/admin' ||
                          statelessPath === '/pipeline' ||
                          statelessPath === '/loanInformation' ||
                          statelessPath === '/profile' ||
                          statelessPath === '/reports' ||
                          statelessPath === '/tools' ||
                          statelessPath === '/quickPrice' ||
                          statelessPath === '/help-support' ||
                          (statelessPath.startsWith('/') && !shouldRenderOutlet && statelessPath !== '/');

  // Handle OAuth callback
  useEffect(() => {
    const handleOAuthCallback = async () => {
      // Check if this is a B2B OAuth callback (NOT magic link)
      const callbackCode = searchParams.get('code');
      const state = searchParams.get('state');
      const sessionToken = searchParams.get('session_token');
      
      // Only handle OAuth flows here (code+state). Magic links (session_token) 
      // should be handled by OAuthCallback.jsx component
      if (callbackCode && state && !sessionToken && statelessPath === '/auth/callback') {
        try {
          const result = await dispatch(handleB2BCallback(callbackCode)).unwrap();

          const user = result?.user;
          // Debug: log the raw callback result to inspect shape for consumer logins
          if (!import.meta.env.PROD) {
            console.log('[Login] handleB2BCallback result:', result);
            console.log('[Login] handleB2BCallback user:', user);
          }
          if (user) {
            const rawRole = user.originalRole || user.role;
            const mappedRole = mapRoleToAppRole(rawRole);

            // Treat consumers with pending invitations or without an active
            // flag as requiring onboarding so we run the same complete-onboarding
            // flow used by employee onboarding.
            const invitationPending = user.invitation_status === 'pending';
            const notActive = !user.is_active;

            const needsOnboarding = Boolean(user.needs_onboarding)
              || user.profile_completed === false
              || (mappedRole === 'consumer' && (invitationPending || notActive));

            const redirectPath = needsOnboarding ? '/onboard' : landingPathForRole(rawRole);
            navigationDoneRef.current = true;
            navigate(buildAppPath(redirectPath), { replace: true });
          }
        } catch (err) {
          console.error('OAuth callback failed:', err);
          navigate(buildAppPath('/login?error=auth_failed'));
        }
      }
    };

    handleOAuthCallback();
  }, [location, dispatch, navigate, searchParams]);

  // Logout & cleanup
  const handleLogout = () => {
    dispatch(logoutAction({ redirect: true }));
  };

  // Reset navigation flag if role mismatch is detected
  useEffect(() => {
    if (roleMismatch) navigationDoneRef.current = false;
  }, [roleMismatch]);

  // Onboarding/navigation logic based on role
  useEffect(() => {
    if (!authLoaded || isLoading) {
      return;
    }

    if (!isAuthenticated || !authState.user || navigationDoneRef.current) {
      return;
    }

    // If this tab sent a magic link and is waiting for the user to open it
    // in another tab, don't auto-redirect — let the user stay on login.
    const awaitingFlag = sessionStorage.getItem('mira_awaiting_magic_link');
    console.log('[Login] useEffect nav check — awaitingMagicLink:', awaitingFlag, 'isAuthenticated:', isAuthenticated);
    if (awaitingFlag) {
      console.log('[Login] Blocked redirect — awaiting magic link in another tab');
      return;
    }

    // Debug: inspect authenticated user state when deciding navigation
    if (!import.meta.env.PROD) {
      console.log('[Login] post-login navigation decision start');
      console.log('[Login] authState.user full object:', JSON.stringify(authState.user, null, 2));
      console.log('[Login] authLoaded:', authLoaded, 'isLoading:', isLoading, 'isAuthenticated:', isAuthenticated);
    }

    const rawRole = authState.user.originalRole || authState.user.role;
    const mappedRole = mapRoleToAppRole(rawRole);

    // Consider consumers with a pending invitation or inactive status as needing onboarding so
    // they are routed through the same `/onboard` flow that completes the
    // invitation and activates the account.
    const invitationPending = authState.user.invitation_status === 'pending';
    const notActive = !authState.user.is_active;

    if (!import.meta.env.PROD) {
      console.log('[Login] Onboarding decision factors:', {
        'user.needs_onboarding': authState.user.needs_onboarding,
        'user.profile_completed': authState.user.profile_completed,
        'user.invitation_status': authState.user.invitation_status,
        'user.is_active': authState.user.is_active,
        rawRole,
        mappedRole,
        invitationPending,
        notActive,
        'Boolean(needs_onboarding)': Boolean(authState.user.needs_onboarding),
        'profile_completed === false': authState.user.profile_completed === false,
        'consumer && (pending || !active)': mappedRole === 'consumer' && (invitationPending || notActive)
      });
    }

    const needsOnboarding = Boolean(authState.user.needs_onboarding)
      || authState.user.profile_completed === false
      || (mappedRole === 'consumer' && (invitationPending || notActive))
      || (mappedRole === 'consumer' && authState.user.onboarding_completed === false);
    const redirectAfterLogin = redirectStorageKey
      ? localStorage.getItem(redirectStorageKey)
      : sessionStorage.getItem('redirectAfterLogin');

    if (needsOnboarding) {
      if (!import.meta.env.PROD) {
        console.log('[Login] ROUTING TO ONBOARDING:', {
          needsOnboarding,
          currentPath: location.pathname,
          userRole: mappedRole,
          invitationStatus: authState.user.invitation_status,
          isActive: authState.user.is_active
        });
      }
      navigationDoneRef.current = true;
      if (statelessPath !== '/onboard') {
        navigate(buildAppPath('/onboard'), { replace: true });
      }
      return;
    }

    const authRoutePaths = new Set([
      '/',
      '/login',
      '/login/b2b',
      '/login/consumer',
      '/account-selection',
      '/onboard',
    ]);
    const isAuthRoute = authRoutePaths.has(statelessPath) || statelessPath.startsWith('/auth/');

    if (!isAuthRoute) {
      return;
    }

    // B2C requirement: after login, consumers should always land on dashboard.
    if (mappedRole === 'consumer') {
      if (redirectStorageKey) {
        localStorage.removeItem(redirectStorageKey);
      }
      sessionStorage.removeItem('redirectAfterLogin');
      const consumerDashboardPath = buildAppPath('/dashboard');
      if (location.pathname !== consumerDashboardPath) {
        navigationDoneRef.current = true;
        navigate(consumerDashboardPath, { replace: true });
      }
      return;
    }

    if (redirectAfterLogin) {
      if (redirectStorageKey) {
        localStorage.removeItem(redirectStorageKey);
      }
      sessionStorage.removeItem('redirectAfterLogin');
      const normalizedRedirect = buildAppPath(redirectAfterLogin);
      if (location.pathname !== normalizedRedirect) {
        navigationDoneRef.current = true;
        navigate(normalizedRedirect, { replace: true });
        return;
      }
    }

    const targetPath = landingPathForRole(rawRole);
    navigationDoneRef.current = true;

    const normalizedTarget = stripStatePrefix(targetPath);
    if (statelessPath !== normalizedTarget) {
      navigate(buildAppPath(targetPath), { replace: true });
    }
  }, [
    isAuthenticated,
    authState.user,
    authLoaded,
    isLoading,
    dispatch,
    navigate,
    location.pathname,
    redirectStorageKey
  ]);

  // Loading state
  if (!authLoaded || isLoading) {
    return <CardLoader />;
  }

  // Error or mismatch
  if (roleMismatch || error) {
    return <ErrorPage onLogout={handleLogout} errorMessage={!!roleMismatch} />;
  }

  if (statelessPath === '/result_page') return <EmailVerified />;
  if (statelessPath === '/session-timeout') return <SessionExpire />;

  // Main render logic
  // If this tab is waiting for a magic link to be opened in another tab,
  // don't switch to the authenticated view — stay on the login page.
  const awaitingMagicLink = typeof sessionStorage !== 'undefined' && sessionStorage.getItem('mira_awaiting_magic_link');

  console.log('[Login] Render check — isAuthenticated:', isAuthenticated, 'awaitingMagicLink:', awaitingMagicLink, 'path:', statelessPath);

  if (isAuthenticated && !awaitingMagicLink && shouldRenderOutletWhenAuthenticated) {
    return <Outlet />;
  }

  if (isAuthenticated && !awaitingMagicLink) {
    // User is authenticated, render the main app
    return (
      <AppShell
        logout={handleLogout}
        data={authState.user}
      />
    );
  }

  // Not authenticated - handle different scenarios

  // For auth-related routes, render the outlet (child routes)
  if (shouldRenderOutlet) {
    return <Outlet />;
  }

  // For protected routes when not authenticated
  if (isProtectedRoute) {

    // Don't redirect or reload, just show the sign-in link page.
    // Store the intended destination for after login
    if (pathname !== '/' && redirectStorageKey) {
      localStorage.setItem(redirectStorageKey, statelessPath);
    } else if (pathname !== '/') {
      sessionStorage.setItem('redirectAfterLogin', statelessPath);
    }

    return <MiraUnifiedLogin />;
  }

  // Default: show the sign-in link page for unauthenticated users
  return <MiraUnifiedLogin />;
};

export default Login;
